import { ButtonProps } from '@mui/material'
import { RuuviTag } from '../types'
import { updateRuuviTag } from '../services/ruuviTagService'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useState } from 'react'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'
import { LoadingButton } from '@mui/lab'

export interface RuuviTagDetachButtonProps extends Omit<ButtonProps, 'children' | 'onClick'> {
  ruuviTag: RuuviTag
  buttonText?: string
  beforeSubmit?: () => void
  afterSubmit?: (ruuviTag: RuuviTag) => void
  onError?: (error: unknown) => void
}

export default function RuuviTagDetachButton({
  ruuviTag,
  buttonText,
  beforeSubmit,
  afterSubmit,
  onError,
  ...props
}: RuuviTagDetachButtonProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('ruuvitag')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The submitting state.
   */
  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  /**
   * Handle the "submit" action.
   */
  async function handleSubmit() {
    try {
      if (ruuviTag?.asset) {
        setSubmitting(true)

        beforeSubmit?.()

        // FIXME: It would be better if we would not need to provide
        // name and company here. Since we are detaching the asset from the project,
        // we might want to reset the name at server-side.
        const updatedRuuviTag = await updateRuuviTag(ruuviTag.id, {
          device: null,
          asset: {
            name: ruuviTag.asset.name,
            company: ruuviTag.asset.companyId,
            project: null,
            level: null,
            lift: null
          }
        })

        dispatch(createSuccessNotification(t('detachRuuviTagSuccess')))

        afterSubmit?.(updatedRuuviTag)
      }
    } catch (error) {
      dispatch(createErrorNotification(t('detachRuuviTagError')))
      onError?.(error)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      {ruuviTag?.asset && (
        <LoadingButton
          variant="contained"
          color="warning"
          loading={isSubmitting}
          {...props}
          onClick={handleSubmit}
        >
          {buttonText ?? t('detachFromProject')}
        </LoadingButton>
      )}
    </>
  )
}
